// CRM 2.0 · M12 Template Builder: Templates list and Approvals (prototype vList / vApprove list).
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import cls from "./Templates.module.css";
import { STATUS, canDo, fmtDate, kindName } from "./templateCommon";
import TemplateSettings from "./TemplateSettings";
import BookingDrafts from "./BookingDrafts";
import CompanyDefaults from "./CompanyDefaults";
import ConfirmModal from "./ConfirmModal";
import { listTemplates, getTemplateBuilderSettings, deleteTemplate, recoverTemplateDraft } from "../../Services/Templates";
import { errorTost, successTost } from "../../Services/Toast";
import { presentLoader, dismissLoader } from "../../Services/loader";

function Templates() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const user = useSelector((state) => state.Auth.user);
  const canSettings = canDo(user, "Settings");
  const canDrafts = canDo(user, "Booking drafts");
  const canDefaults = canDo(user, "Template Linking");
  const tab = params.get("tab") === "approvals" ? "approvals" : params.get("tab") === "settings" && canSettings ? "settings"
    : params.get("tab") === "drafts" && canDrafts ? "drafts" : params.get("tab") === "defaults" && canDefaults ? "defaults" : "templates";
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [deleting, setDeleting] = useState(null); // the list row asked to be deleted
  const [deleted, setDeleted] = useState(null); // Status: Deleted — the deleted drafts (read when chosen)
  const showDeleted = tab === "templates" && status === "DELETED";

  useEffect(() => {
    let live = true;
    presentLoader();
    Promise.all([listTemplates(), getTemplateBuilderSettings()])
      .then(([list, s]) => {
        if (!live) return;
        setRows(list || []);
        setSettings(s?.values || {});
      })
      .catch((e) => errorTost(e.message))
      .finally(() => {
        dismissLoader();
        if (live) setLoaded(true);
      });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!showDeleted) return;
    let live = true;
    listTemplates("DELETED").then((list) => live && setDeleted(list || [])).catch((e) => errorTost(e.message));
    return () => { live = false; };
  }, [showDeleted]);

  async function recover(r) {
    presentLoader();
    try {
      await recoverTemplateDraft(r.docTemplateId);
      successTost(`Recovered draft v${r.versionNo} of ${r.templateName}`);
      setDeleted((list) => (list || []).filter((x) => x.docTemplateId !== r.docTemplateId));
      setRows((await listTemplates()) || []);
    } catch (e) {
      errorTost(e.message);
    } finally {
      dismissLoader();
    }
  }

  const deletedShown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (deleted || []).filter((r) => !q || String(r.templateName).toLowerCase().includes(q));
  }, [deleted, search]);

  const inApproval = rows.filter((r) => r.status === "IN_APPROVAL");
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (tab === "approvals" ? inApproval : rows)
      .filter((r) => !status || r.status === status)
      .filter((r) => !q || String(r.templateName).toLowerCase().includes(q));
  }, [rows, tab, status, search]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={cls.page}>
      <div className={cls.head}>
        <h1 className={cls.title}>Templates</h1>
        {canDo(user, "Create") && (
          <button className={`${cls.btn} ${cls.primary}`} onClick={() => navigate("/home/Templates/new")}>+ New template</button>
        )}
      </div>

      <div className={cls.tabs} role="tablist">
        <button className={`${cls.tab} ${tab === "templates" ? cls.tabOn : ""}`} onClick={() => setParams({})}>Templates</button>
        <button className={`${cls.tab} ${tab === "approvals" ? cls.tabOn : ""}`} onClick={() => setParams({ tab: "approvals" })}>
          Approvals{inApproval.length ? <span className={cls.badge}>{inApproval.length}</span> : null}
        </button>
        {canDrafts && (
          <button className={`${cls.tab} ${tab === "drafts" ? cls.tabOn : ""}`} onClick={() => setParams({ tab: "drafts" })}>Booking drafts</button>
        )}
        {canDefaults && (
          <button className={`${cls.tab} ${tab === "defaults" ? cls.tabOn : ""}`} onClick={() => setParams({ tab: "defaults" })}>Company defaults</button>
        )}
        {canSettings && (
          <button className={`${cls.tab} ${tab === "settings" ? cls.tabOn : ""}`} onClick={() => setParams({ tab: "settings" })}>Settings</button>
        )}
      </div>

      {tab === "settings" ? <TemplateSettings canEdit={canSettings} /> : tab === "drafts" ? <BookingDrafts /> : tab === "defaults" ? <CompanyDefaults /> : <>

      <div className={cls.actionbar}>
        <p className={cls.count}>
          {showDeleted ? `${deletedShown.length} deleted ${deletedShown.length === 1 ? "draft" : "drafts"}`
            : `${shown.length} ${tab === "approvals" ? "waiting for approval" : shown.length === 1 ? "Template" : "Templates"}`}
        </p>
        <div className={cls.filters}>
          {tab === "templates" && (
            <>
              <span className={cls.flab}>Status</span>
              <select className={cls.sel} style={{ width: "18rem" }} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
                <option value="">All</option>
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                <option value="DELETED">Deleted</option>
              </select>
            </>
          )}
          <input className={cls.inp} style={{ width: "26rem" }} placeholder="Search by template name" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search templates" />
        </div>
      </div>

      {showDeleted ? (
        <div className={cls.tblw}>
          <table className={cls.tbl}>
            <thead>
              <tr><th>Template</th><th>Type</th><th>Version</th><th>Deleted by</th><th>Deleted on</th><th className={cls.last} /></tr>
            </thead>
            <tbody>
              {deletedShown.map((r) => (
                <tr key={r.docTemplateId}>
                  <td className={cls.main}>{r.templateName}</td>
                  <td>{kindName(settings, r.templateTypeKey)}</td>
                  <td>v{r.versionNo}</td>
                  <td className={cls.nowrap}>{r.deletedByName || "—"}</td>
                  <td className={cls.nowrap}>{fmtDate(r.deletedAt)}</td>
                  <td className={cls.last}>
                    {canDo(user, "Delete draft") && <button className={cls.pillEdit} onClick={() => recover(r)}>Recover</button>}
                  </td>
                </tr>
              ))}
              {deleted && !deletedShown.length && <tr><td colSpan={6} className={cls.empty}>No deleted drafts</td></tr>}
              {!deleted && <tr><td colSpan={6} className={cls.empty}>Loading…</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
      <div className={cls.tblw}>
        <table className={cls.tbl}>
          <thead>
            <tr>
              <th>Template</th><th>Type</th><th>Version</th><th>Status</th><th>Updated</th><th className={cls.last} />
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.docTemplateId}>
                <td className={cls.main}>
                  {r.templateName}
                  {r.liveVersion ? <div className={cls.hint}>Live: version {r.liveVersion.versionNo}</div> : null}
                </td>
                <td>{kindName(settings, r.templateTypeKey)}</td>
                <td>v{r.versionNo}{r.versions > 1 ? <span className={cls.muted}> of {r.versions}</span> : null}</td>
                <td><span className={`${cls.chip} ${(STATUS[r.status] || {}).chip || ""}`}>{(STATUS[r.status] || {}).label || r.status}</span></td>
                <td>{fmtDate(r.last_updated_at)}</td>
                <td className={cls.last}>
                  <span style={{ display: "inline-flex", gap: ".6rem" }}>
                    <button className={cls.pillEdit} onClick={() => navigate(`/home/Templates/${r.docTemplateId}`)}>
                      {r.status === "IN_APPROVAL" && canDo(user, "Approve") ? "Review" : ["DRAFT", "RETURNED"].includes(r.status) ? "Open" : "View"}
                    </button>
                    {canDo(user, "Delete draft") && r.status === "DRAFT" && (
                      <span title={r.deleteProblem || undefined}>
                        <button className={`${cls.pillEdit} ${cls.danger}`} disabled={!!r.deleteProblem} onClick={() => setDeleting(r)}>Delete template</button>
                      </span>
                    )}
                  </span>
                </td>
              </tr>
            ))}
            {loaded && !shown.length && (
              <tr><td colSpan={6} className={cls.empty}>{tab === "approvals" ? "Nothing waiting for approval" : "No templates yet. Use + New template to upload Legal's Word file."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}
      <p className={cls.hint} style={{ marginTop: "1.6rem" }}>
        {showDeleted ? "A recovered draft comes back as it was, with its version number and all its settings."
          : "A template is used for new drafts once it is Live. Drafts already made keep their version."}
      </p>
      {deleting && (
        <ConfirmModal
          title={deleting.versions > 1 ? `Delete all ${deleting.versions} drafts of ${deleting.templateName}?` : `Delete draft v${deleting.versionNo} of ${deleting.templateName}?`}
          text="You can recover it later: Status: Deleted › Recover."
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            try {
              const r = await deleteTemplate(deleting.templateGroupId);
              successTost(`Deleted ${r.deleted.length === 1 ? `draft v${r.deleted[0].versionNo}` : `${r.deleted.length} drafts`} of ${r.templateName}`);
              setRows((list) => list.filter((x) => x.templateGroupId !== deleting.templateGroupId));
              setDeleted(null);
            } catch (e) {
              errorTost(e.message);
            }
            setDeleting(null);
          }} />
      )}
      </>}
    </div>
  );
}

export default Templates;
